// Read sharing for the NomOS seam (performance, behaviour-neutral):
//
// 1. In-flight de-duplication: identical concurrent reads (same method, URL, body and bearer) share one HTTP request.
//    Reads are GETs and the POST-reads (records/query, records/values, /queries, /catalog/search, /readings/count,
//    .../query). Each caller parses the shared response text itself, so no caller can mutate another's result.
// 2. A short settle window (SETTLE_MS) for query reads: an identical read issued just after the first one answered
//    reuses its answer. This is what collapses the per-page fan-out where several methods ask NomOS the same
//    question one after another while the page loads.
// 3. A session cache (SESSION_TTL_MS) for stable metadata: GET /me, the tenant (GET /tenants/{t}), members and roles,
//    attribute definitions (GET /objects/{type}/attributes), Places config, and records of lookup/reference types.
//
// Invalidation: any write (non-read) drops the settle window entirely and the session-cache tags it touches; a write
// this module cannot attribute (verbs, auth, unknown routes) drops everything. A tenant switch drops everything
// (registerTenantCache in ./client). Only successful (2xx) responses are kept; failures are never cached.
//
// Nothing here changes what NomOS answers: entries are the server's bytes, re-parsed per caller.

export interface RawResponse { ok: boolean; status: number; text: string; url: string }

/** Object types whose records are reference data (rarely written, read on most pages). */
export const LOOKUP_TYPES = new Set([
  'tenant', 'country', 'climate_zone', 'climate_zone_region', 'bidding_zone', 'energy_source', 'emission_factor',
  'seu_category', 'asset_category', 'tariff', 'energy_benchmark', 'building_type_ramp_profile',
]);

export const SESSION_TTL_MS = 5 * 60_000;
export const SETTLE_MS = 1_500;

type Entry = { at: number; ttl: number; tag: string | null; value: RawResponse };
const done = new Map<string, Entry>();
const inflight = new Map<string, Promise<RawResponse>>();
/** bumped on every invalidation; a request that started before it must not store its (possibly stale) answer */
let generation = 0;

let now = () => Date.now();
/** Tests: inject a clock. */
export function setClock(fn: (() => number) | null): void { now = fn ?? (() => Date.now()); }

const OBJ = /^\/objects\/([^/]+)(\/.*)?$/;
const TENANT = /^\/tenants\/([^/]+)(\/[^/]+)?(\/.*)?$/;

/** POST endpoints that only read. */
function isPostRead(path: string): boolean {
  return /\/records\/(query|values)$/.test(path) || path === '/queries' || path === '/catalog/search'
    || path === '/readings/count' || path === '/readings/latest-actual' || /\/[a-z-]+\/query$/.test(path);
}

export function isRead(method: string, path: string): boolean {
  const m = method.toUpperCase();
  return m === 'GET' || (m === 'POST' && isPostRead(stripQuery(path)));
}

const stripQuery = (p: string) => p.split('?')[0];

/** How a read may be shared: a session-cache tag (stable metadata), 'settle' (query read), or null (in-flight only). */
export function readPolicy(method: string, path: string, tenantScoped: boolean): { tag: string; ttl: number } | null {
  const m = method.toUpperCase();
  const p = stripQuery(path);
  if (!tenantScoped) {
    if (m !== 'GET') return null;
    if (p === '/me') return { tag: 'me', ttl: SESSION_TTL_MS };
    const t = TENANT.exec(p);
    if (t && !t[3]) {
      if (!t[2]) return { tag: 'tenant', ttl: SESSION_TTL_MS };
      if (t[2] === '/members') return { tag: 'members', ttl: SESSION_TTL_MS };
      if (t[2] === '/roles') return { tag: 'roles', ttl: SESSION_TTL_MS };
    }
    return null;
  }
  if (m === 'GET' && p === '/places/config') return { tag: 'places', ttl: SESSION_TTL_MS };
  const o = OBJ.exec(p);
  if (o) {
    const type = decodeURIComponent(o[1]);
    if (m === 'GET' && o[2] === '/attributes') return { tag: `attrs:${type}`, ttl: SESSION_TTL_MS };
    if (LOOKUP_TYPES.has(type) && (o[2] === '/records/query' || o[2] === '/records/values')) return { tag: `type:${type}`, ttl: SESSION_TTL_MS };
    if (o[2] === '/records/query' || o[2] === '/records/values') return { tag: 'settle', ttl: SETTLE_MS };
    return null;
  }
  if (m === 'POST' && (p === '/queries' || p === '/readings/count' || p === '/readings/latest-actual' || p === '/catalog/search')) return { tag: 'settle', ttl: SETTLE_MS };
  return null;
}

/** Session-cache tags a write invalidates; 'all' when it cannot be attributed. The settle window is always dropped. */
export function writeTags(path: string, tenantScoped: boolean): string[] | 'all' {
  const p = stripQuery(path);
  if (!tenantScoped) {
    if (p === '/me' || p.startsWith('/me/')) return ['me', 'members'];
    const t = TENANT.exec(p);
    if (t) {
      if (!t[2]) return ['tenant'];
      if (t[2] === '/members' || t[2] === '/invites') return ['members', 'me'];
      if (t[2] === '/roles') return ['roles', 'members', 'me'];
    }
    return 'all'; // /auth/*, unknown
  }
  if (p === '/places/config') return ['places'];
  const o = OBJ.exec(p);
  if (o) {
    const type = decodeURIComponent(o[1]);
    if (o[2]?.startsWith('/attributes')) return [`attrs:${type}`, `type:${type}`];
    return [`type:${type}`];
  }
  return 'all'; // /actions/{verb}/invoke and other routes may write anything
}

const attrListeners = new Set<(type: string | null) => void>();
/** Derived caches (client.attributeIds) follow attribute invalidation. `null` = every type. */
export function onAttributeInvalidation(fn: (type: string | null) => void): void { attrListeners.add(fn); }

/** Drop the settle window plus the given tags (or everything). */
export function invalidate(tags: string[] | 'all'): void {
  generation++;
  inflight.clear();
  if (tags === 'all') { done.clear(); attrListeners.forEach((f) => f(null)); return; }
  const drop = new Set(tags);
  for (const [k, e] of done) if (e.tag === 'settle' || (e.tag !== null && drop.has(e.tag))) done.delete(k);
  for (const t of tags) if (t.startsWith('attrs:')) attrListeners.forEach((f) => f(t.slice(6)));
}

/** Everything (tenant switch, login, logout, tests). */
export function clearReadCache(): void { invalidate('all'); }

/** Called by every transport after a write, successful or not (a failed write may still have partly applied). */
export function noteWrite(path: string, tenantScoped: boolean): void { invalidate(writeTags(path, tenantScoped)); }

/**
 * Run `send` for a read, sharing it with identical concurrent reads and, per `readPolicy`, with later identical reads.
 * `key` must identify method, URL, body and bearer.
 */
export function sharedRead(key: string, policy: { tag: string; ttl: number } | null, send: () => Promise<RawResponse>): Promise<RawResponse> {
  const hit = done.get(key);
  if (hit) {
    if (now() - hit.at < hit.ttl) return Promise.resolve(hit.value);
    done.delete(key);
  }
  const running = inflight.get(key);
  if (running) return running;
  const gen = generation;
  const p = send().then((r) => {
    if (inflight.get(key) === p) inflight.delete(key);
    if (policy && r.ok && gen === generation) done.set(key, { at: now(), ttl: policy.ttl, tag: policy.tag, value: r });
    return r;
  }, (e) => {
    if (inflight.get(key) === p) inflight.delete(key);
    throw e;
  });
  inflight.set(key, p);
  return p;
}

/** Tests/diagnostics: number of live entries. */
export function cacheSize(): { done: number; inflight: number } { return { done: done.size, inflight: inflight.size }; }
